<template>
    <div class="analytics-volatility-heatmap space-y-4">
        <div class="flex flex-wrap items-center gap-3">
            <label class="text-xs font-medium uppercase tracking-wide text-gray-500">
                Range
                <select v-model="range" class="ml-2 rounded-md border border-gray-300 text-sm text-gray-700 px-2 py-1">
                    <option value="7d">7d</option>
                    <option value="30d">30d</option>
                    <option value="90d">90d</option>
                </select>
            </label>
            <label class="text-xs font-medium uppercase tracking-wide text-gray-500">
                Granularity
                <select v-model="granularity" class="ml-2 rounded-md border border-gray-300 text-sm text-gray-700 px-2 py-1">
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                </select>
            </label>
            <button type="button" class="text-sm text-blue-600 hover:text-blue-500" @click="loadHeatmap" :disabled="isLoading">
                Refresh
            </button>
        </div>

        <div v-if="isLoading" class="text-center py-8">
            <div class="inline-flex items-center">
                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span class="text-sm text-gray-600">Loading volatility heatmap…</span>
            </div>
        </div>

        <div v-else-if="errorMessage" class="text-center py-8">
            <svg class="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">Unable to load heatmap</h3>
            <p class="mt-1 text-sm text-gray-500">{{ errorMessage }}</p>
            <button type="button" class="mt-4 text-sm text-blue-600 hover:text-blue-500" @click="loadHeatmap">
                Try Again
            </button>
        </div>

        <div v-else-if="meters.length === 0" class="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-5 text-sm text-gray-600">
            No enrolled meters were found for volatility heatmap visualization.
        </div>

        <template v-else>
            <div class="flex flex-wrap gap-4 text-xs text-gray-600">
                <span class="inline-flex items-center gap-1.5"><span class="inline-block w-3 h-3 rounded-sm bg-emerald-500"></span>Low</span>
                <span class="inline-flex items-center gap-1.5"><span class="inline-block w-3 h-3 rounded-sm bg-sky-500"></span>Moderate</span>
                <span class="inline-flex items-center gap-1.5"><span class="inline-block w-3 h-3 rounded-sm bg-amber-500"></span>High</span>
                <span class="inline-flex items-center gap-1.5"><span class="inline-block w-3 h-3 rounded-sm bg-rose-600"></span>Very high</span>
                <span class="inline-flex items-center gap-1.5"><span class="inline-block w-3 h-3 rounded-sm bg-gray-100 border border-gray-300"></span>No data</span>
            </div>

            <div class="overflow-auto border border-gray-200 rounded-lg">
                <table class="min-w-full border-collapse">
                    <thead class="bg-gray-50">
                        <tr>
                            <th class="sticky left-0 z-20 bg-gray-50 border-b border-r border-gray-200 px-3 py-2 text-left text-xs uppercase tracking-wide text-gray-500">
                                Meter
                            </th>
                            <th
                                v-for="bucket in timeBuckets"
                                :key="'header-' + bucket"
                                class="border-b border-gray-200 px-2 py-2 text-[11px] text-gray-500 whitespace-nowrap"
                            >
                                {{ formatBucket(bucket) }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="row in matrix"
                            :key="'meter-row-' + row.meter_id"
                            :class="{ 'bg-blue-50/40': Number(row.meter_id) === Number(meterId) }"
                        >
                            <td class="sticky left-0 z-10 border-r border-gray-200 px-3 py-2 text-sm text-gray-700 bg-white">
                                <div class="font-medium">{{ meterName(row.meter_id) }}</div>
                                <div v-if="Number(row.meter_id) === Number(meterId)" class="text-xs text-blue-600">Current meter</div>
                            </td>
                            <td
                                v-for="cell in row.values"
                                :key="'cell-' + row.meter_id + '-' + cell.time_bucket"
                                class="border-t border-gray-100 px-1 py-1"
                            >
                                <div
                                    class="w-6 h-6 rounded-sm border border-white shadow-sm"
                                    :style="{ backgroundColor: levelColor(cell.volatility_level) }"
                                    :title="cellTooltip(row.meter_id, cell)"
                                ></div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>
    </div>
</template>

<script>
import axios from 'axios';
import { SonicuCoreMixin } from '@/mixins/SonicuCoreMixin';

export default {
    name: 'AnalyticsVolatilityHeatmap',
    mixins: [SonicuCoreMixin],
    props: {
        meterId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            isLoading: true,
            errorMessage: '',
            range: '30d',
            granularity: 'daily',
            timeBuckets: [],
            meters: [],
            matrix: [],
        };
    },
    watch: {
        range() {
            this.loadHeatmap();
        },
        granularity() {
            this.loadHeatmap();
        },
    },
    mounted() {
        this.loadHeatmap();
    },
    methods: {
        async loadHeatmap() {
            this.isLoading = true;
            this.errorMessage = '';

            try {
                const response = await axios.get(
                    this.company.buildCompanyApiInternalRoute('analytics/volatility-heatmap'),
                    {
                        params: {
                            range: this.range,
                            granularity: this.granularity,
                        },
                    },
                );

                if (!response.data.success) {
                    this.timeBuckets = [];
                    this.meters = [];
                    this.matrix = [];
                    this.isLoading = false;
                    return;
                }

                this.timeBuckets = Array.isArray(response.data.time_buckets) ? response.data.time_buckets : [];
                this.meters = Array.isArray(response.data.meters) ? response.data.meters : [];
                this.matrix = Array.isArray(response.data.matrix) ? response.data.matrix : [];
                this.isLoading = false;
            } catch (error) {
                this.errorMessage = error.response?.data?.message || error.message || 'Failed to load volatility heatmap.';
                this.isLoading = false;
            }
        },
        levelColor(level) {
            const map = {
                low: '#10b981',
                moderate: '#0ea5e9',
                high: '#f59e0b',
                very_high: '#e11d48',
            };
            return map[level] || '#f3f4f6';
        },
        meterName(meterId) {
            const found = this.meters.find((meter) => Number(meter.meter_id) === Number(meterId));
            return found?.meter_name || `Meter ${meterId}`;
        },
        formatBucket(bucket) {
            const date = new Date(`${bucket}T00:00:00Z`);
            if (Number.isNaN(date.getTime())) {
                return bucket;
            }
            return date.toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
            });
        },
        prettyLevel(level) {
            if (!level) {
                return 'No data';
            }
            const text = String(level).replace(/_/g, ' ');
            return text.charAt(0).toUpperCase() + text.slice(1);
        },
        cellTooltip(meterId, cell) {
            const meter = this.meterName(meterId);
            const bucket = this.formatBucket(cell.time_bucket);
            const level = this.prettyLevel(cell.volatility_level);
            const sampleCount = Number(cell.sample_count || 0);
            const recordedAt = cell.recorded_at ? new Date(cell.recorded_at).toLocaleString() : 'N/A';
            return `${meter}\n${bucket}\nRegime: ${level}\nSamples: ${sampleCount}\nRecorded: ${recordedAt}`;
        },
    },
};
</script>

<style scoped>
.analytics-volatility-heatmap {
    width: 100%;
}
</style>
